import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AlarmEventTreeTable } from './AlarmEventTreeTable';
import { RuleRunsTable } from '../alarm-rule/RuleRunsTable';
import { useRuleRuns } from '../alarm-rule/useRuleRuns';
import {
  dataWindowSecondsToValue,
  dataWindowUnitLabels,
  formatDataWindowValue,
  getAlarmRuleErrorMessage,
  getDataWindowUnit,
} from '../filterParameterMapping';

export const AlarmEventViewModal = ({ alarmEvent, onClose, alarmEventService, alarmRuleService, analysisService, eventsExportService, alarmNotificationService, formatTimestampFilter, $translate }) => {
  const [detail, setDetail] = useState(alarmEvent);
  const [alarmEvents, setAlarmEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [environmentColumns, setEnvironmentColumns] = useState([]);
  const [environmentLoading, setEnvironmentLoading] = useState(true);
  const [environmentError, setEnvironmentError] = useState(null);
  const [environmentVersion, setEnvironmentVersion] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [occurrencesVersion, setOccurrencesVersion] = useState(0);
  const [runsActivated, setRunsActivated] = useState(false);
  const mountedRef = useRef(false);
  const summaryRequestRef = useRef(0);
  const exportingRef = useRef(false);
  const hasRuleId = detail.ruleId != null && /^\d+$/.test(String(detail.ruleId));
  const runsActive = activeTab === 1 && hasRuleId;
  const { cancel: cancelRuns, restart: restartRuns, ...runs } = useRuleRuns(detail.ruleId, alarmRuleService, runsActivated, runsActive);
  const [loaders] = useState(() => ({
    occurrences: params => alarmEventService.getOccurrences(alarmEvent.id, params),
    events: ({ occurrenceIndex, ...params }) => alarmEventService.getOccurrenceEvents(alarmEvent.id, occurrenceIndex, params),
  }));

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setEnvironmentLoading(true);
    setEnvironmentError(null);
    const load = async () => {
      try {
        const metadata = await analysisService.envDataVariables();
        const names = metadata.map(field => field?.element_display_name || field?.name);
        if (names.some(name => typeof name !== 'string' || !name.trim())) {
          throw new Error('Invalid environment data metadata response');
        }
        if (!cancelled && mountedRef.current) setEnvironmentColumns([...new Set(names)]);
      } catch (loadError) {
        if (cancelled || !mountedRef.current) return;
        const message = getAlarmRuleErrorMessage(loadError, 'Environment columns could not be loaded. The Show/Hide list may be incomplete.');
        setEnvironmentError(message);
        alarmNotificationService.error('Failed to load environment columns', message);
      } finally {
        if (!cancelled && mountedRef.current) setEnvironmentLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [analysisService, alarmNotificationService, environmentVersion]);

  useEffect(() => {
    let cancelled = false;
    const request = ++summaryRequestRef.current;
    setLoading(true);
    setError(null);
    const load = async () => {
      try {
        const summary = await alarmEventService.getSummary(alarmEvent.id);
        if (cancelled || !mountedRef.current || request !== summaryRequestRef.current) return;
        const summaries = Array.isArray(summary) ? summary : summary ? [summary] : [];
        setAlarmEvents(summaries);
        if (summaries[0]) setDetail({ ...alarmEvent, ...summaries[0] });
      } catch (loadError) {
        if (cancelled || !mountedRef.current || request !== summaryRequestRef.current) return;
        const message = getAlarmRuleErrorMessage(loadError, 'Failed to load alarm event details.');
        setError(message);
        alarmNotificationService.error('Failed to load alarm event details', message);
      } finally {
        if (!cancelled && mountedRef.current && request === summaryRequestRef.current) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [alarmEvent, alarmEventService, alarmNotificationService, occurrencesVersion]);

  const selectOccurrences = event => {
    event.preventDefault();
    cancelRuns();
    summaryRequestRef.current += 1;
    setLoading(true);
    setOccurrencesVersion(version => version + 1);
    setActiveTab(0);
  };

  const selectRuns = event => {
    event.preventDefault();
    restartRuns();
    setRunsActivated(true);
    setActiveTab(1);
  };

  const exportAlarm = async exportFunction => {
    if (exportingRef.current || loading || !mountedRef.current) return;
    exportingRef.current = true;
    setExporting(true);
    try {
      await exportFunction({}, `alarm-event-${alarmEvent.id}`, `alarm-event/${alarmEvent.id}/export`);
    } catch (exportError) {
      if (mountedRef.current) {
        alarmNotificationService.error('Failed to export alarm event', getAlarmRuleErrorMessage(exportError, 'Please try again.'));
      }
    } finally {
      exportingRef.current = false;
      if (mountedRef.current) setExporting(false);
    }
  };

  const close = () => {
    mountedRef.current = false;
    cancelRuns();
    onClose();
  };
  const unit = getDataWindowUnit(detail.dataWindowSeconds);
  const dataWindow = formatDataWindowValue(dataWindowSecondsToValue(detail.dataWindowSeconds, unit));

  return (
    <div className="alarm-event-view-modal" data-testid="alarm-event-view-modal">
      <div className="modal-header">
        <h3 className="modal-title">Alarm for {detail.name}</h3>
        <button type="button" className="close" aria-label="close" onClick={close} data-testid="alarm-event-view-header-close-button">
          <span aria-hidden="true">x</span>
        </button>
      </div>
      <div className="modal-body alarm-event-view-modal__body" data-testid="alarm-event-view-modal-body">
        <div className="alarm-event-view-modal__meta" data-testid="alarm-event-view-modal-meta">
          <span><strong>ID:</strong> {detail.id}</span>
          <span><strong>Rule ID:</strong> {detail.ruleId}</span>
          <span><strong>Rule Type:</strong> {detail.rule?.ruleType || '-'}</span>
          <span><strong>Occurrence Total Count:</strong> {detail.occurrenceTotalCount}</span>
          <span><strong>Consist Total Count:</strong> {detail.consistTotalCount}</span>
          <span><strong>Event Total Count:</strong> {detail.historyEventTotalCount}</span>
          <span><strong>Triggered Count:</strong> {detail.triggeredCount}</span>
          <span><strong>Data Window:</strong> {dataWindow} {dataWindowUnitLabels[unit]}</span>
          <span><strong>Created:</strong> {formatTimestampFilter(detail.createdTs)}</span>
          <span><strong>Updated:</strong> {formatTimestampFilter(detail.updatedTs)}</span>
          <span><strong>Handled:</strong> {detail.isHandled ? 'Yes' : 'No'}</span>
        </div>
        <div className="alarm-event-view-modal__tabs alarm-view-modal-tabs" data-testid="alarm-event-view-tabs">
          <ul className="nav nav-tabs nav-justified" role="tablist">
            <li className={activeTab === 0 ? 'active' : ''}>
              <a href="#alarm-event-occurrences-panel" id="alarm-event-occurrences-tab" role="tab"
                aria-controls="alarm-event-occurrences-panel" aria-selected={activeTab === 0}
                onClick={selectOccurrences} data-testid="alarm-event-view-occurrences-tab">
                {$translate.instant('alarm-event-view.event-occurrences-tab')}
              </a>
            </li>
            <li className={activeTab === 1 ? 'active' : ''}>
              <a href="#alarm-event-runs-panel" id="alarm-event-runs-tab" role="tab"
                aria-controls="alarm-event-runs-panel" aria-selected={activeTab === 1}
                onClick={selectRuns} data-testid="alarm-event-view-rule-runs-tab">
                {$translate.instant('alarm-rule-view.rule-runs-tab')}
              </a>
            </li>
          </ul>
          <div className="tab-content alarm-event-view-modal__tab-content">
            <div className={`tab-pane alarm-event-view-modal__tab-panel${activeTab === 0 ? ' active' : ''}`}
              id="alarm-event-occurrences-panel" role="tabpanel" aria-labelledby="alarm-event-occurrences-tab"
              data-testid="alarm-event-view-occurrences-panel">
              <div className="nav-content-custom alarm-event-view-modal__occurrences" data-testid="alarm-event-view-occurrences-content">
                {activeTab === 0 && environmentLoading && (
                  <output className="help-block" data-testid="alarm-event-view-environment-loading">Loading environment columns...</output>
                )}
                {activeTab === 0 && environmentError && (
                  <div className="alert alert-danger" role="alert" data-testid="alarm-event-view-environment-error">
                    <p>{environmentError}</p>
                    <p>The Show/Hide list may be incomplete until environment columns are loaded.</p>
                    <button type="button" className="btn btn-secondary" disabled={environmentLoading}
                      onClick={() => setEnvironmentVersion(version => version + 1)} data-testid="alarm-event-view-environment-retry-button">
                      <i className="fas fa-sync-alt fa-fw" aria-hidden="true" /> Retry
                    </button>
                  </div>
                )}
                {activeTab === 0 && loading && (
                  <output data-testid="alarm-event-view-occurrences-loading">
                    <i className="fas fa-spinner fa-pulse fa-fw" aria-hidden="true" /> {$translate.instant('alarm-event-view.loading')}
                  </output>
                )}
                {activeTab === 0 && !loading && error && (
                  <div className="alert alert-danger" role="alert" data-testid="alarm-event-view-occurrences-error">{error}</div>
                )}
                {activeTab === 0 && !loading && !error && (
                  <AlarmEventTreeTable
                    key={occurrencesVersion}
                    alarmEvents={alarmEvents}
                    environmentColumns={environmentColumns}
                    lazyLoading
                    onLoadOccurrences={loaders.occurrences}
                    onLoadOccurrenceEvents={loaders.events}
                    isExporting={exporting}
                    isExportDisabled={loading}
                    exportExcelLabel={$translate.instant('export.excel')}
                    exportCsvLabel={$translate.instant('export.csv')}
                    onExportExcel={() => exportAlarm(eventsExportService.exportToExcel)}
                    onExportCsv={() => exportAlarm(eventsExportService.exportToCsv)}
                    rootLevel="occurrence"
                  />
                )}
              </div>
            </div>
            <div className={`tab-pane alarm-event-view-modal__tab-panel${activeTab === 1 ? ' active' : ''}`}
              id="alarm-event-runs-panel" role="tabpanel" aria-labelledby="alarm-event-runs-tab"
              data-testid="alarm-event-view-rule-runs-panel">
              <div className="nav-content-custom" data-testid="alarm-event-view-rule-runs-content">
                {activeTab === 1 && !hasRuleId && loading && (
                  <output data-testid="alarm-event-view-rule-runs-summary-loading">{$translate.instant('alarm-event-view.loading')}</output>
                )}
                {activeTab === 1 && !hasRuleId && !loading && (
                  <div className="alert alert-danger" role="alert" data-testid="alarm-event-view-rule-runs-unavailable">
                    {error || $translate.instant('alarm-event-view.rule-unavailable')}
                  </div>
                )}
                {runsActivated && hasRuleId && (
                  <RuleRunsTable {...runs} active={runsActive} $translate={$translate} formatTimestampFilter={formatTimestampFilter} testIdPrefix="alarm-event-view-rule-runs" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={close} data-testid="alarm-event-view-close-button">
          <i className="fas fa-times fa-fw" aria-hidden="true" /> Close
        </button>
      </div>
    </div>
  );
};

AlarmEventViewModal.propTypes = {
  alarmEvent: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  alarmEventService: PropTypes.object.isRequired,
  alarmRuleService: PropTypes.object.isRequired,
  analysisService: PropTypes.object.isRequired,
  eventsExportService: PropTypes.object.isRequired,
  alarmNotificationService: PropTypes.object.isRequired,
  formatTimestampFilter: PropTypes.func.isRequired,
  $translate: PropTypes.func.isRequired,
};